import { useEffect, useState, useCallback } from 'react';
import SettingsService from '../services/settingsService';

/**
 * The user's saved home location, for the "Home" accommodation option and any
 * other surface that wants a one-tap home (GETTING_AROUND_PLAN "Home as a
 * first-class anchor"). Home lives in Settings → `location.{home,
 * home_place_id, home_lat, home_lng}` — precise when picked via autocomplete.
 *
 * Returns `{ home, coordinates, placeId, isSet, loading, setHome }`:
 *   - home        the display string ('' when unset)
 *   - coordinates { lat, lng } | null (only when a geocoded pick was saved)
 *   - placeId     Google place id | null
 *   - isSet       true once a non-empty home string exists
 *   - setHome({ name, coordinates, placeId, countryCode })  persists to Settings (the funnel
 *                 that captures home the first time from the accommodation
 *                 picker), resolving to the fresh home object
 *
 * A module-level cache + subscriber set means every mounted instance shares one
 * fetch and all update together when `setHome` runs — so setting home from one
 * editor immediately reflects everywhere.
 */

let cache = null;        // resolved home object, or null until first load
let inflight = null;     // de-dupes concurrent first loads
// Bumped at an auth boundary (clearHomeLocation): a load started for the
// previous account must neither be cached nor shown when it lands (BH-10-01).
let generation = 0;
const subscribers = new Set();
const NO_HOME = { home: '', coordinates: null, placeId: null, isSet: false };

const toHome = (settings) => {
  const loc = settings?.location || {};
  const lat = loc.home_lat;
  const lng = loc.home_lng;
  return {
    home: loc.home || '',
    coordinates: (Number.isFinite(lat) && Number.isFinite(lng)) ? { lat, lng } : null,
    placeId: loc.home_place_id || null,
    isSet: !!(loc.home && String(loc.home).trim()),
  };
};

const publish = (next) => {
  cache = next;
  subscribers.forEach((fn) => fn(next));
};

async function loadHome() {
  if (cache) return cache;
  if (!inflight) {
    const gen = generation;
    const request = SettingsService.getUserSettings()
      .then((settings) => { const h = toHome(settings); if (gen === generation) cache = h; return h; })
      .catch(() => toHome(null))
      .finally(() => { if (inflight === request) inflight = null; });
    inflight = request;
  }
  return inflight;
}

export default function useHomeLocation() {
  const [state, setState] = useState(cache);

  useEffect(() => {
    let alive = true;
    subscribers.add(setState);
    const gen = generation;
    if (!cache) loadHome().then((h) => { if (alive && gen === generation) setState(h); });
    else setState(cache);
    return () => { alive = false; subscribers.delete(setState); };
  }, []);

  const setHome = useCallback(async ({ name, coordinates, placeId, countryCode } = {}) => {
    // Read-modify-write the full settings blob (same shape ProfileTab persists),
    // so we set only the location fields and leave everything else intact.
    // A read that didn't reach the server must not be written back whole: it
    // is defaults or this device's copy, and saving it would wipe the
    // account's other settings (BH-10-02). Nothing is saved or published.
    const { settings, fromServer } = await SettingsService.getUserSettingsWithSource();
    if (!fromServer) {
      return { ...(cache || toHome(null)), saved: false };
    }
    const location = { ...(settings.location || {}) };
    location.home = name || '';
    location.home_place_id = placeId || null;
    location.home_lat = coordinates?.lat ?? null;
    location.home_lng = coordinates?.lng ?? null;
    // ISO-2 country when the caller knows it ("Use my current location"
    // reverse-geocodes one); the server's coordinate path uses it when there
    // is no place id to look a country up by (BH-10-04).
    location.home_country_code = countryCode || null;
    // saveUserSettings reports failure by returning { success: false } rather
    // than throwing, so callers that need to know (the /welcome flow gates a
    // step on it) get `saved` alongside the home fields. Existing callers read
    // only the home fields and are unaffected.
    const result = await SettingsService.saveUserSettings({ ...settings, location });
    const next = toHome({ ...settings, location });
    publish(next);
    // The app-wide settings-change signal (AuthContext, useHomeSearch,
    // useProfileData… all listen) — a home set here is a settings save like
    // any other and other surfaces must not keep a stale copy.
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('settingsUpdated'));
    }
    // `saved` is a per-call result, deliberately not part of the published
    // cache — a cached one would go stale and read as the outcome of a save
    // that happened much earlier.
    return { ...next, saved: result?.success !== false };
  }, []);

  const resolved = state || { home: '', coordinates: null, placeId: null, isSet: false };
  return { ...resolved, loading: state === null, setHome };
}

/** Test/mutation hook: drop the cached home so the next mount refetches. */
export const invalidateHomeLocation = () => { cache = null; inflight = null; generation += 1; };

/**
 * Auth boundary (logout, token expiry — invalidateSessionCaches): forget this
 * account's home and tell every mounted instance there is none, so the next
 * account on this tab can't see it or save it as its own (BH-10-01). Mounted
 * instances get "no home, not loading" rather than a refetch: nobody is
 * signed in yet, and a fresh mount after the next sign-in loads that
 * account's home.
 */
export const clearHomeLocation = () => {
  invalidateHomeLocation();
  subscribers.forEach((fn) => fn(NO_HOME));
};

// Any settings save elsewhere (the Settings modal dispatches `settingsUpdated`
// on save — the app-wide convention) invalidates the module cache so the next
// read refetches; mounted instances refresh live. Without this, a home edited
// in ProfileTab left "Use my home" serving the stale address for the whole
// session (audit finding).
if (typeof window !== 'undefined') {
  window.addEventListener('settingsUpdated', () => {
    cache = null;
    inflight = null;
    const gen = generation;
    if (subscribers.size > 0) loadHome().then((h) => { if (gen === generation) publish(h); });
  });
}
